import { AddCircleIcon } from '@synergy/theme-next';
import { ArrayHelpers, FieldArray, FormikProps, useFormikContext } from 'formik';
import { Box, Divider, Grid, Stack } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    FormikPoolDatePicker,
    Headline,
    PoolButton,
    PoolDropzone,
    PoolFormikAutocomplete,
    PoolFormikNumberTextfield,
    PoolFormikTextfield,
    PoolIconButton,
    useIsMobile,
} from '@synergy/components-next';
import { ReactElement } from 'react';
import { TrashIcon } from '@synergy/theme';
import { base64encode, fields } from '@synergy/utils';
import dayjs from 'dayjs';

import { SingleInquiryFileList } from './single-inquiry-file-list';
import { SingleInquiryFormValues } from './models/single-inquiry-form-values';
import { accountDeliveryAddressesSelector } from '../../../store/configuration-selectors';
import { useAppSelector } from '../../../../store/store';

type FieldArrayHelpers = ArrayHelpers & {
    form: FormikProps<SingleInquiryFormValues>;
    name: string;
};

export const fieldNames = fields<SingleInquiryFormValues>();

export const SingleInquiryContent = (): ReactElement => {
    const deliveryAddresses = useAppSelector(accountDeliveryAddressesSelector);
    const formikProps = useFormikContext<SingleInquiryFormValues>();
    const intl = useIntl();
    const isMobile = useIsMobile();

    const handleDrop = async (acceptedFiles: File[]): Promise<void> => {
        const existingFileNames = formikProps.values.files.map(file => file.name);
        const deduplicated = acceptedFiles.filter(file => !existingFileNames.includes(file.name));
        const promises = deduplicated.map(file =>
            base64encode(file).then(result => ({ name: file.name, contentBase64: result }))
        );
        const uploadedFiles = await Promise.all(promises);
        formikProps.setFieldValue(fieldNames.files, [...formikProps.values.files, ...uploadedFiles]);
    };

    const handleFileRemove = (filename: string): void => {
        formikProps.setFieldValue(
            fieldNames.files,
            formikProps.values.files.filter(file => file.name !== filename)
        );
    };

    const selectOptions = deliveryAddresses.map(address => ({
        value: address.id,
        displayValue: address.fullAddressWithTitle,
    }));

    const tomorrow = dayjs().add(1, 'day');

    const handleRemoveItem = (index: number, arrayHelpers: FieldArrayHelpers): void => {
        if (arrayHelpers.form.values.items.length === 1) {
            arrayHelpers.form.setFieldValue(fieldNames.items, [{ id: 1, articleName: '', quantity: null }]);
        } else {
            arrayHelpers.handleRemove(index)();
        }
    };

    return (
        <Grid container={true} spacing={{ xs: 3, sm: 5 }}>
            <Grid container={true} item={true} xs={12} spacing={2}>
                <Grid item={true} xs={12}>
                    <Headline translationId="app.singleInquiry.deliveryInfo" />
                </Grid>
                <Grid item={true} sm={8.5} xs={12}>
                    <PoolFormikAutocomplete
                        name={fieldNames.deliveryAddressId}
                        label={<FormattedMessage id="app.singleInquiry.deliveryAddress" />}
                        options={selectOptions}
                        required={true}
                    />
                </Grid>

                <Grid item={true} sm={3.5} xs={12}>
                    <FormikPoolDatePicker
                        name={fieldNames.deliveryDate}
                        label={<FormattedMessage id="app.singleInquiry.deliveryDate" />}
                        minDate={tomorrow}
                    />
                </Grid>
            </Grid>

            <Grid container={true} item={true} xs={12}>
                {!isMobile && (
                    <Grid item={true} xs={12}>
                        <Headline translationId="app.singleInquiry.productInfo" />
                    </Grid>
                )}
                <Grid item={true} xs={12} pt={0}>
                    <FieldArray name={fieldNames.items}>
                        {(arrayHelpers: FieldArrayHelpers) => (
                            <Stack spacing={isMobile ? 2 : 1}>
                                <Box>
                                    <Stack
                                        divider={!isMobile && <Divider />}
                                        sx={{ width: '100%' }}
                                        spacing={isMobile ? 3 : 0}
                                    >
                                        {formikProps.values.items.map((item, index) => {
                                            const itemsPrefix = `${fieldNames.items}.${index}`;

                                            if (isMobile) {
                                                return (
                                                    <MobileSingleInquiryContent
                                                        key={item.id}
                                                        index={index}
                                                        itemsPrefix={itemsPrefix}
                                                        onRemoveItem={index => handleRemoveItem(index, arrayHelpers)}
                                                    />
                                                );
                                            }

                                            return (
                                                <Stack
                                                    key={item.id}
                                                    direction="row"
                                                    spacing={2}
                                                    sx={{
                                                        padding: theme => theme.spacing(2),
                                                        alignItems: 'center',
                                                        width: '100%',
                                                    }}
                                                >
                                                    <Box typography="h6" sx={{ width: 56 }}>
                                                        {index + 1}
                                                    </Box>
                                                    <PoolFormikTextfield
                                                        name={`${itemsPrefix}.articleName`}
                                                        placeholder={intl.formatMessage({
                                                            id: 'app.singleInquiry.product',
                                                        })}
                                                        label={intl.formatMessage({
                                                            id: 'app.singleInquiry.product',
                                                        })}
                                                        required={true}
                                                        fullWidth={true}
                                                        sx={{ flex: '1 1 755px' }}
                                                    />
                                                    <PoolFormikNumberTextfield
                                                        name={`${itemsPrefix}.quantity`}
                                                        placeholder={intl.formatMessage({
                                                            id: 'app.singleInquiry.quantity',
                                                        })}
                                                        label={intl.formatMessage({
                                                            id: 'app.singleInquiry.quantity',
                                                        })}
                                                        required={true}
                                                        fullWidth={true}
                                                        sx={{ flex: '1 1 200px' }}
                                                    />
                                                    <PoolIconButton
                                                        color="primary"
                                                        onClick={() => handleRemoveItem(index, arrayHelpers)}
                                                    >
                                                        <TrashIcon />
                                                    </PoolIconButton>
                                                </Stack>
                                            );
                                        })}
                                    </Stack>
                                    {!isMobile && <Divider />}
                                </Box>
                                <PoolButton
                                    startIcon={<AddCircleIcon />}
                                    variant="text"
                                    onClick={arrayHelpers.handlePush({
                                        id:
                                            formikProps.values.items.reduce(
                                                (max, item) => (item.id > max.id ? item : max),
                                                formikProps.values.items[0]
                                            ).id + 1,
                                        articleName: '',
                                        quantity: null,
                                    })}
                                    sx={{
                                        width: 'fit-content',
                                        marginLeft: '24px !important',
                                    }}
                                >
                                    <FormattedMessage id="app.singleInquiry.addRow" />
                                </PoolButton>
                            </Stack>
                        )}
                    </FieldArray>
                </Grid>
            </Grid>

            <Grid container={true} item={true} spacing={2}>
                <Grid item={true} xs={12}>
                    <Headline translationId="app.singleInquiry.additionalInformation" />
                </Grid>
                <Grid item={true} sm={6} xs={12}>
                    <PoolFormikTextfield
                        name={fieldNames.noteFromCustomer}
                        label={<FormattedMessage id="app.singleInquiry.noteFromCustomer" />}
                        fullWidth={true}
                        multiline={true}
                        minRows={5}
                        maxRows={50}
                    />
                </Grid>
                <Grid item={true} sm={6} xs={12}>
                    <PoolDropzone onDrop={handleDrop} />
                </Grid>
                {!isMobile && <Grid item={true} sm={6} />}
                <Grid item={true} sm={6} xs={12}>
                    <SingleInquiryFileList
                        fileNames={formikProps.values.files.map(file => file.name)}
                        onDelete={handleFileRemove}
                    />
                </Grid>
            </Grid>
        </Grid>
    );
};

const MobileSingleInquiryContent = ({
    key,
    index,
    itemsPrefix,
    onRemoveItem,
}: {
    key: number;
    index: number;
    itemsPrefix: string;
    onRemoveItem: (index: number) => void;
}): ReactElement => {
    const intl = useIntl();

    return (
        <Stack
            key={key}
            spacing={2}
            sx={{
                alignItems: 'center',
                width: '100%',
            }}
        >
            <Headline translationId="app.singleInquiry.product.mobile" values={{ index: index + 1 }}>
                <PoolIconButton size="small" color="primary" onClick={() => onRemoveItem(index)}>
                    <TrashIcon />
                </PoolIconButton>
            </Headline>
            <PoolFormikTextfield
                name={`${itemsPrefix}.articleName`}
                placeholder={intl.formatMessage({
                    id: 'app.singleInquiry.product',
                })}
                label={intl.formatMessage({
                    id: 'app.singleInquiry.product',
                })}
                required={true}
                fullWidth={true}
            />
            <PoolFormikNumberTextfield
                name={`${itemsPrefix}.quantity`}
                placeholder={intl.formatMessage({
                    id: 'app.singleInquiry.quantity',
                })}
                label={intl.formatMessage({
                    id: 'app.singleInquiry.quantity',
                })}
                required={true}
                fullWidth={true}
            />
        </Stack>
    );
};
